import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import logoSwimteamPro from '../assets/logo_swimteampro.png';

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: 'Helvetica',
  },
  header: {
    marginBottom: 30,
    textAlign: 'center',
  },
  logo: {
    width: '120',
    height: '120',
    marginBottom: 15,
    alignSelf: 'center',
    objectFit: 'contain',
  },
  footerLogo: {
    width: '60',
    height: '60',
    marginTop: 10,
    alignSelf: 'center',
    objectFit: 'contain',
  },
  title: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  subtitle: {
    fontSize: 12,
    marginBottom: 5,
  },
  swimmerSection: {
    marginBottom: 15,
    padding: 10,
    borderBottom: 1,
    borderBottomColor: '#cccccc',
  },
  swimmerName: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  swimmerStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 5,
    flexWrap: 'wrap',
  },
  statInfo: {
    fontSize: 10,
    flex: 1,
    minWidth: '33%',
    marginBottom: 3,
  },
  detail: {
    fontSize: 10,
    marginBottom: 3,
  },
  lapsTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginTop: 5,
    marginBottom: 3,
  },
  lapRow: {
    flexDirection: 'row',
    marginBottom: 2,
    justifyContent: 'space-between',
    paddingRight: 20,
  },
  lapInfo: {
    fontSize: 9,
    flex: 1,
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: 'center',
  },
  summarySection: {
    marginBottom: 20,
    padding: 10,
    backgroundColor: '#f5f5f5',
    borderRadius: 5,
  },
  summaryTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#1976d2',
  },
  summaryStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
    flexWrap: 'wrap',
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: '#cccccc',
    marginVertical: 15,
  }
});

const ResultsPDF = ({ results, formatTime, totalTime, poolLength, raceDistance }) => {
  const institutionLogo = localStorage.getItem('institutionLogo');
  const institutionName = localStorage.getItem('institutionName');
  const eventName = localStorage.getItem('eventName');
  const currentYear = new Date().getFullYear();

  const calcularEstatisticasGerais = () => {
    const tempoTotalEquipe = results.reduce((total, result) => {
      return total + (result.laps?.reduce((sum, lap) => sum + lap, 0) || 0);
    }, 0);
    
    const metragemTotal = results.reduce((total, result) => {
      return total + ((result.laps?.length || 0) * (raceDistance || poolLength));
    }, 0);

    const totalVoltas = Math.ceil(metragemTotal / poolLength);

    return { tempoTotalEquipe, metragemTotal, totalVoltas };
  };

  const { tempoTotalEquipe, metragemTotal, totalVoltas } = calcularEstatisticasGerais();

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          {institutionLogo ? (
            <Image style={styles.logo} src={institutionLogo} />
          ) : (
            <Image style={styles.logo} src={logoSwimteamPro} />
          )}
          <Text style={styles.title}>
            {institutionName || 'SWIMTEAM PRO'}
          </Text>
          <Text style={styles.subtitle}>
            {eventName || `${currentYear}`}
          </Text>
        </View>

        <View style={styles.summarySection}>
          <Text style={styles.summaryTitle}>Resumo da Competição</Text>
          <View style={styles.summaryStats}>
            <Text style={styles.statInfo}>
              Tamanho da Piscina:{'\n'}{poolLength}m
            </Text>
            <Text style={styles.statInfo}>
              Distância da Prova:{'\n'}{raceDistance || poolLength}m
            </Text>
            <Text style={styles.statInfo}>
              Tempo Total de Prova:{'\n'}{formatTime(totalTime)}
              {'\n\n'}
            </Text>
            <Text style={styles.statInfo}>
              Total de Voltas:{'\n'}{totalVoltas}
            </Text>
            <Text style={styles.statInfo}>
              Metragem Total:{'\n'}{metragemTotal}m
            </Text>
            <Text style={styles.statInfo}>
              Tempo Total de Competição:{'\n'}{formatTime(tempoTotalEquipe)}
            </Text>
          </View>
        </View>

        <View style={styles.divider} />

        {results && results.map((result, index) => {
          const totalSwimTime = result.laps?.reduce((sum, lap) => sum + lap, 0) || 0;
          const distance = (result.laps?.length || 0) * (raceDistance || poolLength);
          const totalLaps = Math.ceil(distance / poolLength);
          const speedMS = distance / (totalSwimTime / 1000);
          const speedKMH = speedMS * 3.6;
          const averageTime = result.laps?.length ? totalSwimTime / result.laps.length : 0;

          return (
            <View key={result.swimmer} style={styles.swimmerSection}>
              <Text style={styles.swimmerName}>
                {index + 1}º Lugar: {result.swimmer}
              </Text>
              
              <View style={styles.swimmerStats}>
                <Text style={styles.statInfo}>
                  Voltas na Piscina: {totalLaps}
                </Text>
                <Text style={styles.statInfo}>
                  Distância: {distance}m
                </Text>
                <Text style={styles.statInfo}>
                  Tempo Médio: {formatTime(averageTime)}
                </Text>
                <Text style={styles.statInfo}>
                  Tempo Total: {formatTime(totalSwimTime)}
                </Text>
                <Text style={styles.statInfo}>
                  Veloc.(m/s): {speedMS.toFixed(2)}
                </Text>
                <Text style={styles.statInfo}>
                  Veloc.(km/h): {speedKMH.toFixed(2)}
                </Text>
              </View>

              {result.laps && result.laps.length > 0 && (
                <>
                  <Text style={styles.lapsTitle}>Detalhes das Voltas:</Text>
                  {result.laps.map((time, idx) => {
                    const previousLapTime = idx > 0 ? result.laps[idx - 1] : time;
                    const difference = time - previousLapTime;
                    
                    return (
                      <View key={idx} style={styles.lapRow}>
                        <Text style={styles.lapInfo}>
                          Registro {idx + 1}
                        </Text>
                        <Text style={styles.lapInfo}>
                          Distância: {raceDistance || poolLength}m
                        </Text>
                        <Text style={styles.lapInfo}>
                          Tempo: {formatTime(time)}
                        </Text>
                        <Text style={styles.lapInfo}>
                          Diferença: {idx === 0 ? '-' : (
                            `${difference > 0 ? '+' : ''}${formatTime(difference)}`
                          )}
                        </Text>
                      </View>
                    );
                  })}
                </>
              )}
            </View>
          );
        })}

        <View style={styles.footer}>
          <Text style={styles.detail}>
            Relatório gerado em: {new Date().toLocaleDateString('pt-BR')} às {new Date().toLocaleTimeString('pt-BR')}
          </Text>
          {institutionLogo && (
            <Image style={styles.footerLogo} src={logoSwimteamPro} />
          )}
        </View>
      </Page>
    </Document>
  );
};

export default ResultsPDF;